import Phaser from 'phaser';
import { GAME_WIDTH, FONT_FAMILY, getGameHeight, BOARD_ROWS, BOARD_COLS } from '../config/constants';
import { padGrid } from '../levels/gridUtils';
import { getGameplayColorHex } from '../config/colors';
import { LEVEL_COUNT, LEVEL_METADATA, loadLevel } from '../levels/imageLevels';
import type { ImageLevel } from '../levels/types';
import { crispText } from '../ui/textUtils';
import { drawGradientBackground } from '../ui/background';
import { launchGameScene } from '../types/GameSceneLaunchData';
import { fadeToScene } from '../ui/sceneTransition';
import { setUrlState } from '../utils/urlState';
import { getHighestCompletedLevel } from '../systems/progressStore';

const THUMB_SIZE = 240; // fixed visual size for all thumbnails
const THUMB_GAP = 32;
const COLS_PER_ROW = 3;
const LEVELS_PER_PAGE = 48; // divisible by 3 columns = 16 rows per page

interface ThumbnailOptions {
  container: Phaser.GameObjects.Container;
  x: number;
  y: number;
  thumbSize: number;
  grid: number[][];
  name: string;
  levelNum: number;
  visualPalette?: string[];
  visualGrid?: number[][];
  preloadedLevel?: ImageLevel;
  solverUnknown?: boolean;
}

export class LevelSelectScene extends Phaser.Scene {
  private currentPage = 0;
  private totalPages = 0;
  private pageContainer!: Phaser.GameObjects.Container;
  private staticContainer!: Phaser.GameObjects.Container;
  private pageIndicator!: Phaser.GameObjects.Text;
  private prevBtnGfx!: Phaser.GameObjects.Graphics;
  private nextBtnGfx!: Phaser.GameObjects.Graphics;
  private prevBtnText!: Phaser.GameObjects.Text;
  private nextBtnText!: Phaser.GameObjects.Text;

  // Scroll state — managed manually so we can reset on page change
  private scrollY = 0;
  private maxScroll = 0;
  private dragStartY = 0;
  private containerStartY = 0;
  private wheelHandler?: (pointer: Phaser.Input.Pointer, objs: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void;
  private pointerDownHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerMoveHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerUpHandler?: (pointer: Phaser.Input.Pointer) => void;

  /** True when user is dragging to scroll (suppresses tap actions) */
  private isDragging = false;

  /** Loading indicator text */
  private loadingText?: Phaser.GameObjects.Text;

  /** Tracks which page render is current (prevents stale async renders) */
  private renderGeneration = 0;

  /** Abort controller for cancelling in-flight level fetches on page change */
  private loadAbortController?: AbortController;

  /** Filter: when true, show only levels PerfectSolver couldn't decide (solverUnknown === true). */
  private filterSolverUnknown = false;

  /** Graphics handle for the filter toggle button background (redrawn on state change). */
  private filterBtnGfx?: Phaser.GameObjects.Graphics;

  /**
   * Indices into LEVEL_METADATA for the active view. When filter is OFF, this is
   * the identity list [0, 1, 2, ...]; when ON, only solver-unknown indices.
   * Populated on every renderPage() call so page navigation stays consistent.
   */
  private viewIndices: number[] = [];

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  create(data?: { page?: number }): void {
    this.rebuildViewIndices();
    this.totalPages = Math.ceil(this.viewIndices.length / LEVELS_PER_PAGE);

    // Determine initial page: URL/scene data > saved progress > page 0
    if (data?.page != null && data.page >= 0 && data.page < this.totalPages) {
      this.currentPage = data.page;
    } else {
      const savedLevel = getHighestCompletedLevel();
      this.currentPage = savedLevel > 0
        ? Math.min(Math.floor(savedLevel / LEVELS_PER_PAGE), this.totalPages - 1)
        : 0;
    }

    // ── Background gradient ──────────────────────────────────────────────
    drawGradientBackground(this);

    // Page content container (thumbnails — gets recreated per page)
    this.pageContainer = this.add.container(0, 0);

    // Static container for header elements (sits on top of page content)
    this.staticContainer = this.add.container(0, 0);
    this.staticContainer.setDepth(10);

    // ── Fixed header bar background ─────────────────────────────────────
    const headerHeight = 112;
    const headerBg = this.add.graphics();
    headerBg.fillStyle(0x1a1a2e, 0.95);
    headerBg.fillRect(0, 0, GAME_WIDTH, headerHeight);
    // Subtle bottom border
    headerBg.fillStyle(0x3a3a6a, 0.5);
    headerBg.fillRect(0, headerHeight - 2, GAME_WIDTH, 2);
    this.staticContainer.add(headerBg);

    const centerX = GAME_WIDTH / 2;
    const headerCenterY = headerHeight / 2;

    // ── Home button (top-left) ───────────────────────────────────────────
    const homeBtnW = 96;
    const homeBtnH = 72;
    const homeBtnX = 72;
    const homeBtnY = headerCenterY;

    const homeGfx = this.add.graphics();
    homeGfx.fillStyle(0x555577, 1);
    homeGfx.fillRoundedRect(homeBtnX - homeBtnW / 2, homeBtnY - homeBtnH / 2, homeBtnW, homeBtnH, 16);
    homeGfx.fillStyle(0xffffff, 0.08);
    homeGfx.fillRoundedRect(homeBtnX - homeBtnW / 2 + 4, homeBtnY - homeBtnH / 2 + 2, homeBtnW - 8, homeBtnH * 0.4, { tl: 12, tr: 12, bl: 0, br: 0 });
    this.staticContainer.add(homeGfx);

    this.staticContainer.add(crispText(this.add.text(homeBtnX, homeBtnY, '\u2190', {
      fontFamily: FONT_FAMILY,
      fontSize: '40px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5));

    const homeHit = this.add.rectangle(homeBtnX, homeBtnY, homeBtnW, homeBtnH, 0x000000, 0);
    homeHit.setInteractive({ useHandCursor: true });
    this.staticContainer.add(homeHit);

    homeHit.on('pointerup', () => {
      if (!this.isDragging) {
        fadeToScene(this, () => this.scene.start('MenuScene'));
      }
    });

    // ── Solver-unknown filter toggle (top-right) ─────────────────────────
    // Flags the 300 levels the PerfectSolver couldn't decide at 5-min budget.
    // When enabled, only those levels are shown in the picker.
    const filterBtnW = 160;
    const filterBtnH = 72;
    const filterBtnX = GAME_WIDTH - 96;
    const filterBtnY = headerCenterY;

    this.filterBtnGfx = this.add.graphics();
    this.staticContainer.add(this.filterBtnGfx);
    this.drawFilterButton(filterBtnX, filterBtnY, filterBtnW, filterBtnH);

    const filterLabel = crispText(this.add.text(filterBtnX, filterBtnY, '? only', {
      fontFamily: FONT_FAMILY,
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.staticContainer.add(filterLabel);

    const filterHit = this.add.rectangle(filterBtnX, filterBtnY, filterBtnW, filterBtnH, 0x000000, 0);
    filterHit.setInteractive({ useHandCursor: true });
    this.staticContainer.add(filterHit);

    filterHit.on('pointerup', () => {
      if (this.isDragging) return;
      this.filterSolverUnknown = !this.filterSolverUnknown;
      this.rebuildViewIndices();
      this.totalPages = Math.max(1, Math.ceil(this.viewIndices.length / LEVELS_PER_PAGE));
      this.currentPage = 0;
      this.drawFilterButton(filterBtnX, filterBtnY, filterBtnW, filterBtnH);
      this.renderPage();
    });

    // ── Navigation in header ─────────────────────────────────────────────
    const navBtnW = 96;
    const navBtnH = 72;

    // Prev button
    this.prevBtnGfx = this.add.graphics();
    this.staticContainer.add(this.prevBtnGfx);

    this.prevBtnText = crispText(this.add.text(centerX - 220, headerCenterY, '\u25C0', {
      fontFamily: FONT_FAMILY,
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.staticContainer.add(this.prevBtnText);

    const prevBtn = this.add.rectangle(centerX - 220, headerCenterY, navBtnW, navBtnH, 0x000000, 0);
    prevBtn.setInteractive({ useHandCursor: true });
    this.staticContainer.add(prevBtn);

    prevBtn.on('pointerup', () => {
      if (!this.isDragging && this.currentPage > 0) {
        this.currentPage--;
        this.renderPage();
      }
    });

    // Next button
    this.nextBtnGfx = this.add.graphics();
    this.staticContainer.add(this.nextBtnGfx);

    this.nextBtnText = crispText(this.add.text(centerX + 220, headerCenterY, '\u25B6', {
      fontFamily: FONT_FAMILY,
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.staticContainer.add(this.nextBtnText);

    const nextBtn = this.add.rectangle(centerX + 220, headerCenterY, navBtnW, navBtnH, 0x000000, 0);
    nextBtn.setInteractive({ useHandCursor: true });
    this.staticContainer.add(nextBtn);

    nextBtn.on('pointerup', () => {
      if (!this.isDragging && this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.renderPage();
      }
    });

    // Page indicator (centered between nav arrows)
    this.pageIndicator = crispText(this.add.text(centerX, headerCenterY, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '34px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.staticContainer.add(this.pageIndicator);

    // Render initial page
    this.renderPage();

    // Fade in
    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  /** Remove any previously attached scroll listeners */
  private removeScrollListeners(): void {
    if (this.wheelHandler) {
      this.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    if (this.pointerDownHandler) {
      this.input.off('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = undefined;
    }
    if (this.pointerMoveHandler) {
      this.input.off('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = undefined;
    }
  }

  /** Attach scroll listeners for the current page container */
  private setupScrolling(contentHeight: number): void {
    this.removeScrollListeners();

    if (contentHeight <= getGameHeight()) return;

    this.scrollY = 0;
    this.maxScroll = contentHeight - getGameHeight() + 40;
    this.pageContainer.setY(0);

    this.wheelHandler = (_pointer: Phaser.Input.Pointer, _objs: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, this.maxScroll);
      this.pageContainer.setY(-this.scrollY);
    };
    this.input.on('wheel', this.wheelHandler);

    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      this.dragStartY = pointer.y;
      this.containerStartY = this.pageContainer.y;
      this.isDragging = false;
    };
    this.input.on('pointerdown', this.pointerDownHandler);

    this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const dy = this.dragStartY - pointer.y;
      if (Math.abs(dy) > 10) {
        this.isDragging = true;
        this.scrollY = Phaser.Math.Clamp(-this.containerStartY + dy, 0, this.maxScroll);
        this.pageContainer.setY(-this.scrollY);
      }
    };
    this.input.on('pointermove', this.pointerMoveHandler);
  }

  /**
   * Rebuild the list of level indices for the active view (filtered or full).
   * Call whenever the filter toggles.
   */
  private rebuildViewIndices(): void {
    if (this.filterSolverUnknown) {
      this.viewIndices = LEVEL_METADATA
        .map((m, i) => (m.solverUnknown ? i : -1))
        .filter(i => i >= 0);
    } else {
      this.viewIndices = Array.from({ length: LEVEL_COUNT }, (_, i) => i);
    }
  }

  /** Draw the filter toggle button, styled to reflect current filter state. */
  private drawFilterButton(x: number, y: number, w: number, h: number): void {
    if (!this.filterBtnGfx) return;
    this.filterBtnGfx.clear();
    const baseColor = this.filterSolverUnknown ? 0xc28b2f : 0x555577;
    this.filterBtnGfx.fillStyle(baseColor, 1);
    this.filterBtnGfx.fillRoundedRect(x - w / 2, y - h / 2, w, h, 16);
    this.filterBtnGfx.fillStyle(0xffffff, 0.08);
    this.filterBtnGfx.fillRoundedRect(x - w / 2 + 4, y - h / 2 + 2, w - 8, h * 0.4, { tl: 12, tr: 12, bl: 0, br: 0 });
  }

  private renderPage(): void {
    const centerX = GAME_WIDTH / 2;
    const thumbSize = THUMB_SIZE;
    const headerHeight = 112;

    // Destroy old page content
    this.pageContainer.removeAll(true);
    this.pageContainer.setY(0);

    // Update page indicator and URL state
    const totalVisible = this.viewIndices.length;
    const pageLabel = this.filterSolverUnknown
      ? `Page ${this.currentPage + 1} / ${this.totalPages}  (${totalVisible} ?)`
      : `Page ${this.currentPage + 1} / ${this.totalPages}`;
    this.pageIndicator.setText(pageLabel);
    setUrlState({ scene: 'levelSelect', page: String(this.currentPage) });

    // Update nav button states
    this.drawNavButton(this.prevBtnGfx, centerX - 220, headerHeight / 2, 96, 72, this.currentPage > 0);
    this.drawNavButton(this.nextBtnGfx, centerX + 220, headerHeight / 2, 96, 72, this.currentPage < this.totalPages - 1);
    this.prevBtnText.setAlpha(this.currentPage > 0 ? 1 : 0.3);
    this.nextBtnText.setAlpha(this.currentPage < this.totalPages - 1 ? 1 : 0.3);

    // Compute which LEVEL_METADATA indices to render on this page
    const pageStart = this.currentPage * LEVELS_PER_PAGE;
    const pageEnd = Math.min(pageStart + LEVELS_PER_PAGE, this.viewIndices.length);
    const pageIndices = this.viewIndices.slice(pageStart, pageEnd);

    // Show loading indicator
    if (this.loadingText) this.loadingText.destroy();

    // Empty-state message (e.g. filter active but no matches)
    if (pageIndices.length === 0) {
      this.loadingText = crispText(this.add.text(centerX, getGameHeight() / 2,
        this.filterSolverUnknown ? 'No solver-unknown levels.' : 'No levels.', {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: '#8888aa',
      })).setOrigin(0.5).setDepth(5);
      return;
    }

    this.loadingText = crispText(this.add.text(centerX, getGameHeight() / 2, 'Loading...', {
      fontFamily: FONT_FAMILY,
      fontSize: '36px',
      color: '#8888aa',
    })).setOrigin(0.5).setDepth(5);

    // Track this render generation to prevent stale async renders
    const gen = ++this.renderGeneration;

    // Abort any in-flight fetches from previous page loads
    if (this.loadAbortController) this.loadAbortController.abort();
    this.loadAbortController = new AbortController();
    const { signal } = this.loadAbortController;

    // Load level data for this page asynchronously. `pageIndices` may be
    // non-contiguous when filter is on, so we fetch one-by-one instead of
    // using loadLevels() which assumes a contiguous [start, start+count) range.
    Promise.all(pageIndices.map(idx => loadLevel(idx, signal))).then(levels => {
      // If user has navigated away from this page, discard results
      if (gen !== this.renderGeneration) return;

      // Remove loading indicator
      if (this.loadingText) {
        this.loadingText.destroy();
        this.loadingText = undefined;
      }

      const startY = headerHeight + 24;
      const totalGridWidth = COLS_PER_ROW * thumbSize + (COLS_PER_ROW - 1) * THUMB_GAP;
      const gridStartX = (GAME_WIDTH - totalGridWidth) / 2;
      const rowHeight = thumbSize + THUMB_GAP + 56;

      for (let i = 0; i < levels.length; i++) {
        const level = levels[i];
        const globalIdx = pageIndices[i];
        const col = i % COLS_PER_ROW;
        const row = Math.floor(i / COLS_PER_ROW);

        const x = gridStartX + col * (thumbSize + THUMB_GAP);
        const y = startY + row * rowHeight;

        this.createThumbnail({
          container: this.pageContainer,
          x, y, thumbSize,
          grid: level.grid,
          name: level.name,
          levelNum: globalIdx + 1,
          visualPalette: level.visualPalette,
          visualGrid: level.visualGrid,
          preloadedLevel: level,
          solverUnknown: LEVEL_METADATA[globalIdx]?.solverUnknown === true,
        }, i);
      }

      // Set up scrolling for this page
      const totalRows = Math.ceil(levels.length / COLS_PER_ROW);
      const contentHeight = startY + totalRows * rowHeight + 40;
      this.setupScrolling(contentHeight);
    }).catch(err => {
      // Silently ignore aborted fetches (user navigated to a different page)
      if (err?.name === 'AbortError') return;
      // If user has navigated away, ignore the error
      if (gen !== this.renderGeneration) return;

      console.error(`Failed to load levels for page ${this.currentPage + 1}:`, err);

      // Remove loading indicator and show error
      if (this.loadingText) {
        this.loadingText.destroy();
        this.loadingText = undefined;
      }
      const errorText = crispText(this.add.text(centerX, getGameHeight() / 2, 'Failed to load levels.\nTap to retry.', {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: '#ff6666',
        align: 'center',
      })).setOrigin(0.5).setDepth(5);
      this.pageContainer.add(errorText);

      // Allow tap-to-retry
      const retryHit = this.add.rectangle(centerX, getGameHeight() / 2, GAME_WIDTH, 200, 0x000000, 0);
      retryHit.setInteractive({ useHandCursor: true });
      retryHit.setDepth(5);
      this.pageContainer.add(retryHit);
      retryHit.on('pointerup', () => {
        if (!this.isDragging) this.renderPage();
      });
    });
  }

  private drawNavButton(gfx: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, enabled: boolean): void {
    gfx.clear();
    gfx.fillStyle(enabled ? 0x555577 : 0x333344, 1);
    gfx.fillRoundedRect(x - w / 2, y - h / 2, w, h, 16);
    if (enabled) {
      gfx.fillStyle(0xffffff, 0.08);
      gfx.fillRoundedRect(x - w / 2 + 4, y - h / 2 + 2, w - 8, h * 0.4, { tl: 12, tr: 12, bl: 0, br: 0 });
    }
  }

  private createThumbnail(options: ThumbnailOptions, pageIdx: number): void {
    const { container, x, y, thumbSize, grid: rawGrid, name, levelNum, visualPalette, visualGrid: rawVisualGrid, preloadedLevel, solverUnknown } = options;
    // Pad to board dimensions so thumbnail proportions match gameplay.
    // Uses BOARD_ROWS/BOARD_COLS as defaults; future per-level sizes can override.
    const grid = padGrid(rawGrid, BOARD_ROWS, BOARD_COLS);
    const vGrid = rawVisualGrid ? padGrid(rawVisualGrid, BOARD_ROWS, BOARD_COLS) : undefined;
    const gridRows = grid.length;
    const gridCols = grid[0]?.length ?? gridRows;
    const cellSize = thumbSize / Math.max(gridRows, gridCols);

    // Center the grid within the thumbnail if not square
    const drawnW = gridCols * cellSize;
    const drawnH = gridRows * cellSize;
    const offsetX = (thumbSize - drawnW) / 2;
    const offsetY = (thumbSize - drawnH) / 2;

    // Shadow
    const shadow = this.add.rectangle(
      x + thumbSize / 2 + 4, y + thumbSize / 2 + 6,
      thumbSize + 8, thumbSize + 8,
      0x000000,
    ).setAlpha(0);
    container.add(shadow);

    // Background
    const bg = this.add.rectangle(
      x + thumbSize / 2, y + thumbSize / 2,
      thumbSize + 8, thumbSize + 8,
      0x1e1e3a,
    );
    bg.setStrokeStyle(3, 0x3a3a5a);
    bg.setAlpha(0);
    container.add(bg);

    // Draw each cell — scale to fit thumbSize
    const gfx = this.add.graphics();
    gfx.setAlpha(0);
    gfx.fillStyle(0x2a2a4a, 1);
    gfx.fillRect(x, y, thumbSize, thumbSize);
    for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        const val = grid[r][c];
        if (val < 0) continue;
        let color: number;
        if (visualPalette && vGrid) {
          const vi = vGrid[r]?.[c] ?? -1;
          const hex = vi >= 0 ? visualPalette[vi] : undefined;
          color = hex ? parseInt(hex.slice(1), 16) : 0xffffff;
        } else {
          color = getGameplayColorHex(val);
        }
        gfx.fillStyle(color, 1);
        gfx.fillRect(
          x + offsetX + c * cellSize,
          y + offsetY + r * cellSize,
          Math.ceil(cellSize),
          Math.ceil(cellSize),
        );
      }
    }
    container.add(gfx);

    // Level number + name
    const label = crispText(this.add.text(x + thumbSize / 2, y + thumbSize + 16, `${levelNum}. ${name}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '24px',
      color: '#8888aa',
    })).setOrigin(0.5).setAlpha(0);
    container.add(label);

    // Solver-unknown badge: small amber "?" in the top-right corner of the thumbnail.
    // Signals that PerfectSolver timed out on seed 42 — winnability undecided.
    let badgeBg: Phaser.GameObjects.Graphics | undefined;
    let badgeText: Phaser.GameObjects.Text | undefined;
    if (solverUnknown) {
      const badgeSize = 32;
      const badgeX = x + thumbSize - 8;
      const badgeY = y + 8;
      badgeBg = this.add.graphics();
      badgeBg.fillStyle(0xc28b2f, 1);
      badgeBg.fillCircle(badgeX, badgeY + badgeSize / 2, badgeSize / 2);
      badgeBg.lineStyle(2, 0x000000, 0.4);
      badgeBg.strokeCircle(badgeX, badgeY + badgeSize / 2, badgeSize / 2);
      badgeBg.setAlpha(0);
      container.add(badgeBg);
      badgeText = crispText(this.add.text(badgeX, badgeY + badgeSize / 2, '?', {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
      })).setOrigin(0.5).setAlpha(0);
      container.add(badgeText);
    }

    // Interactive overlay
    const hitArea = this.add.rectangle(
      x + thumbSize / 2, y + thumbSize / 2,
      thumbSize + 8, thumbSize + 8,
      0x000000, 0,
    );
    container.add(hitArea);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => bg.setStrokeStyle(4, 0x3498db));
    hitArea.on('pointerout', () => bg.setStrokeStyle(3, 0x3a3a5a));
    hitArea.on('pointerup', () => {
      if (!this.isDragging) {
        // Pass pre-loaded level data to avoid a second load
        launchGameScene(this, { level: levelNum, levelData: preloadedLevel });
      }
    });

    // Staggered entrance animation — spring scale + fade in
    const animElements: Array<{ setScale: (s: number) => unknown }> = [shadow, bg, gfx, label];
    if (badgeBg) animElements.push(badgeBg);
    if (badgeText) animElements.push(badgeText);
    animElements.forEach(el => el.setScale(0.85));

    // Shadow fades to 0.25
    this.tweens.add({
      targets: shadow,
      alpha: 0.25,
      scaleX: 1,
      scaleY: 1,
      duration: 350,
      delay: pageIdx * 25,
      ease: 'Back.easeOut',
    });

    // Other visible elements fade to 1
    const fadeTargets: Phaser.GameObjects.GameObject[] = [bg, gfx, label];
    if (badgeBg) fadeTargets.push(badgeBg);
    if (badgeText) fadeTargets.push(badgeText);
    this.tweens.add({
      targets: fadeTargets,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 350,
      delay: pageIdx * 25,
      ease: 'Back.easeOut',
    });
  }
}
